import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../../../api/admin/users';
import { rolesApi, userRolesApi, permissionOverridesApi } from '../../../api/admin/roles';
import { approvalLimitsApi } from '../../../api/admin/approvalLimits';
import { ApiError } from '../../../api/client';
import { DataTable } from '../../../components/DataTable';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { PermissionScope } from '../../../types/admin';

const scopes: PermissionScope[] = ['own', 'project', 'department', 'location', 'country', 'all'];

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const id = Number(userId);
  const canManageUsers = usePermission('admin.users.manage');
  const canManageApprovals = usePermission('admin.approvals.manage');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const userQuery = useQuery({ queryKey: ['user', id], queryFn: () => usersApi.get(id) });
  const allRolesQuery = useQuery({ queryKey: ['roles'], queryFn: () => rolesApi.list() });
  const userRolesQuery = useQuery({ queryKey: ['user-roles', id], queryFn: () => userRolesApi.list(id) });
  const overridesQuery = useQuery({ queryKey: ['user-overrides', id], queryFn: () => permissionOverridesApi.list(id) });

  const assignRoleMutation = useMutation({
    mutationFn: (roleId: number) => userRolesApi.assign(id, roleId),
    onSuccess: () => {
      showSuccess('Role assigned.');
      queryClient.invalidateQueries({ queryKey: ['user-roles', id] });
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to assign role.'),
  });

  const removeRoleMutation = useMutation({
    mutationFn: (roleId: number) => userRolesApi.remove(id, roleId),
    onSuccess: () => {
      showSuccess('Role removed.');
      queryClient.invalidateQueries({ queryKey: ['user-roles', id] });
    },
    // Surfaces the last-admin-protection guard if this role is what
    // made this user the sole active admin.
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to remove role.'),
  });

  const [selectedRoleId, setSelectedRoleId] = useState('');
  const assignedRoleIds = new Set(userRolesQuery.data?.data.map((r) => r.id));
  const availableRoles = allRolesQuery.data?.data.filter((r) => r.is_active && !assignedRoleIds.has(r.id)) ?? [];

  return (
    <div className="page">
      <Link to="/admin/users">&larr; Back to Users</Link>

      {userQuery.data && (
        <div className="page-header">
          <h1>{userQuery.data.data.full_name}</h1>
        </div>
      )}
      {userQuery.data && <p className="muted">{userQuery.data.data.email}</p>}

      <section className="card">
        <h2>Roles</h2>
        <p className="muted">Assigning HR Manager automatically grants approval authority for Workforce Requests and Contracts. Other approval-related roles or modules still need a limit set manually below, under Approval Limits.</p>
        {userRolesQuery.data && (
          <DataTable
            rows={userRolesQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage="No roles assigned."
            columns={[
              { header: 'Role', render: (r) => r.name },
              {
                header: '',
                render: (r) =>
                  canManageUsers ? (
                    <button className="link-button" onClick={() => removeRoleMutation.mutate(r.id)}>Remove</button>
                  ) : null,
              },
            ]}
          />
        )}
        {canManageUsers && availableRoles.length > 0 && (
          <div className="inline-form">
            <select value={selectedRoleId} onChange={(e) => setSelectedRoleId(e.target.value)}>
              <option value="">Select a role…</option>
              {availableRoles.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            <button
              disabled={!selectedRoleId || assignRoleMutation.isPending}
              onClick={() => selectedRoleId && assignRoleMutation.mutate(Number(selectedRoleId))}
            >
              Assign
            </button>
          </div>
        )}
      </section>

      <section className="card">
        <h2>Permission Overrides</h2>
        {overridesQuery.data && (
          <DataTable
            rows={overridesQuery.data.data}
            rowKey={(o) => o.id}
            emptyMessage="No permission overrides."
            columns={[
              { header: 'Permission', render: (o) => o.permission_key },
              { header: 'Scope', render: (o) => o.scope },
              { header: 'Grant/Revoke', render: (o) => (o.is_granted ? 'Granted' : 'Revoked') },
            ]}
          />
        )}
      </section>

      <section className="card">
        <ApprovalLimitsSection userId={id} canManage={canManageApprovals} />
      </section>
    </div>
  );
}

const limitFormSchema = z.object({
  module: z.string().min(2, 'Enter a module name, e.g. general'),
  maxAmount: z.coerce.number().positive('Must be greater than zero.'),
  currencyCode: z.string().length(3, 'Use a 3-letter currency code, e.g. NGN'),
  scope: z.enum(['own', 'project', 'department', 'location', 'country', 'all']),
});
type LimitFormValues = z.infer<typeof limitFormSchema>;

function ApprovalLimitsSection({ userId, canManage }: { userId: number; canManage: boolean }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);

  const limitsQuery = useQuery({ queryKey: ['user-approval-limits', userId], queryFn: () => approvalLimitsApi.list(userId) });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<LimitFormValues>({
    resolver: zodResolver(limitFormSchema),
    defaultValues: { currencyCode: 'NGN', scope: 'own' },
  });

  const createMutation = useMutation({
    mutationFn: (values: LimitFormValues) => approvalLimitsApi.create(userId, values),
    onSuccess: () => {
      showSuccess('Approval limit saved.');
      queryClient.invalidateQueries({ queryKey: ['user-approval-limits', userId] });
      reset();
      setShowForm(false);
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to save approval limit.'),
  });

  return (
    <>
      <div className="page-header">
        <h2>Approval Limits</h2>
        {canManage && <button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'Add Limit'}</button>}
      </div>

      {showForm && (
        <form className="inline-form" onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
          <label>
            Module
            <select {...register('module')}>
              <option value="">- select a module -</option>
              <option value="workforce_request">Workforce Request</option>
              <option value="contracts">Contracts</option>
              <option value="project">Project (budget approval)</option>
              <option value="project_amendment">Project Amendment</option>
              <option value="recruitment">Recruitment</option>
              <option value="general">General</option>
            </select>
            {errors.module && <span className="field-error">{errors.module.message}</span>}
          </label>
          <label>
            Max amount
            <input type="number" step="0.01" {...register('maxAmount')} />
            {errors.maxAmount && <span className="field-error">{errors.maxAmount.message}</span>}
          </label>
          <label>
            Currency
            <input {...register('currencyCode')} />
            {errors.currencyCode && <span className="field-error">{errors.currencyCode.message}</span>}
          </label>
          <label>
            Scope
            <select {...register('scope')}>
              {scopes.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Saving…' : 'Save'}</button>
        </form>
      )}

      {limitsQuery.data && (
        <DataTable
          rows={limitsQuery.data.data}
          rowKey={(l) => l.id}
          emptyMessage="No approval limits set."
          columns={[
            { header: 'Module', render: (l) => l.module },
            { header: 'Max Amount', render: (l) => `${l.currency_code} ${l.max_amount}` },
            { header: 'Scope', render: (l) => l.scope },
            { header: 'Status', render: (l) => (l.is_active ? 'Active' : 'Inactive') },
          ]}
        />
      )}
    </>
  );
}
